import { Star, MapPin, BadgeCheck, ChevronRight, Clock, Users, IndianRupee } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { motion } from "framer-motion";

export interface CAProfile {
  id: string;
  name: string;
  firm_name: string | null;
  city: string;
  state: string | null;
  experience_years: number;
  specializations: string[];
  services: string[];
  pricing_hourly: number | null;
  pricing_monthly: number | null;
  bio: string | null;
  email: string | null;
  is_verified: boolean;
  avg_rating: number;
  total_reviews: number;
  total_clients: number;
  avatar_url: string | null;
  updated_at?: string;
}

export function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`h-3.5 w-3.5 ${s <= Math.round(rating) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`}
        />
      ))}
      <span className="text-xs font-medium text-foreground ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

export function CACard({ ca, onSelect, rank }: { ca: CAProfile; onSelect: () => void; rank: number }) {
  const isTopRated = rank <= 3 && ca.avg_rating >= 4.0 && ca.total_reviews >= 1;

  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.15 }}>
      <Card className={`bg-card border-border hover:border-accent/30 transition-all cursor-pointer group relative ${isTopRated ? "ring-1 ring-yellow-400/30" : ""}`} onClick={onSelect}>
        {isTopRated && (
          <div className="absolute -top-2 -right-2 z-10">
            <Badge className="bg-yellow-500/90 text-yellow-50 text-[9px] px-1.5 py-0.5 gap-0.5 shadow-sm">
              <Star className="h-2.5 w-2.5 fill-current" /> Top Rated
            </Badge>
          </div>
        )}
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-lg ${isTopRated ? "bg-yellow-500/10 text-yellow-600" : "bg-primary/10 text-primary"}`}>
              {ca.name.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground truncate">{ca.name}</h3>
                {ca.is_verified && <BadgeCheck className="h-4 w-4 text-accent flex-shrink-0" />}
              </div>
              {ca.firm_name && <p className="text-[11px] text-muted-foreground truncate">{ca.firm_name}</p>}
              <div className="flex items-center gap-3 mt-1.5">
                <span className="text-[11px] text-muted-foreground flex items-center gap-1"><MapPin className="h-3 w-3" /> {ca.city}</span>
                <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {ca.experience_years}y exp</span>
                <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> {ca.total_clients} clients</span>
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-accent transition-colors flex-shrink-0 mt-1" />
          </div>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/30">
            <div className="flex items-center gap-2">
              <StarRating rating={ca.avg_rating} />
              <span className="text-[10px] text-muted-foreground">({ca.total_reviews})</span>
            </div>
            <div className="flex items-center gap-2">
              {ca.pricing_hourly && (
                <span className="text-[11px] font-medium text-foreground flex items-center gap-0.5">
                  <IndianRupee className="h-3 w-3" />{ca.pricing_hourly.toLocaleString("en-IN")}/hr
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-1 mt-2.5">
            {ca.specializations.slice(0, 3).map((s) => (
              <Badge key={s} variant="secondary" className="text-[10px] px-1.5 py-0 h-5">{s}</Badge>
            ))}
            {ca.specializations.length > 3 && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5">+{ca.specializations.length - 3}</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
